import type { PoseKeypoint } from './pose.ts';
import { normalizePose } from './pose.ts';
import {
  isRenderableRemoteNormCoord,
  normalizeCoordFromNativeJson,
  normalizeRadiusFromNativeJson,
} from './normCoord.ts';

/** One fruit as sent by native (normalized 0–1 coordinates). */
export interface NativeFruitPayload {
  readonly x: number;
  readonly y: number;
  readonly is_bomb: boolean;
  readonly is_hit: boolean;
  /**
   * Стабильный идентификатор фрукта с нативной стороны (Swift/Kotlin Int64 / hash и т.д.).
   * **Рекомендация:** для значений за пределами ±`Number.MAX_SAFE_INTEGER` присылать **`id` строкой** в JSON
   * (например `"id":"-7653039059964581361"`), иначе `JSON.parse` в браузере округляет число.
   * Число в допустимом диапазоне или строка цифр — оба варианта принимаются.
   * Если поля нет — трекинг по индексу в массиве `fruits`.
   */
  readonly id?: number | string;
  /** Degrees; whole fruit orientation before slice. */
  readonly rotation?: number;
  /** `apple` | `peach` | `pineapple` | `strawberry` | `watermelon` | `kiwi`; omit / null for bomb. */
  readonly image?: string | null;
  /** After slice — normalized center of first half; null until sliced. */
  readonly x1?: number | null;
  readonly y1?: number | null;
  readonly x2?: number | null;
  readonly y2?: number | null;
  /** Degrees for each half after slice. */
  readonly rotation1?: number | null;
  readonly rotation2?: number | null;
}

/** Native often sends (0,0) for half centers before real coords exist — that maps to top-left; ignore. */
function halfCenterIsSentinelOrigin(x: number, y: number): boolean {
  return x === 0 && y === 0;
}

/**
 * Half centers must sit near the whole-fruit center (same normalized space as x,y).
 * With several fruits, native sometimes leaves x1/y1/x2/y2 near (0,0) or stale — TV would draw in the corner.
 */
function halfCenterPlausibleVsMain(
  mainX: number,
  mainY: number,
  hx: number,
  hy: number,
): boolean {
  const manhattan = Math.abs(hx - mainX) + Math.abs(hy - mainY);
  // Buggy/stale half coords (e.g. near 0,0) are ~0.8+ from a mid-screen fruit; real halves stay near the cut.
  return manhattan <= 0.55;
}

/** True when native drives both half positions (TV skips local half particles). */
export function nativeFruitHasRenderableHalves(f: NativeFruitPayload): boolean {
  if (!f.is_hit || f.is_bomb) return false;
  if (
    f.x1 == null ||
    f.y1 == null ||
    f.x2 == null ||
    f.y2 == null ||
    f.rotation1 == null ||
    f.rotation2 == null
  ) {
    return false;
  }
  if (halfCenterIsSentinelOrigin(f.x1, f.y1) || halfCenterIsSentinelOrigin(f.x2, f.y2)) {
    return false;
  }
  if (
    !halfCenterPlausibleVsMain(f.x, f.y, f.x1, f.y1) ||
    !halfCenterPlausibleVsMain(f.x, f.y, f.x2, f.y2)
  ) {
    return false;
  }
  return (
    isRenderableRemoteNormCoord(f.x1) &&
    isRenderableRemoteNormCoord(f.y1) &&
    isRenderableRemoteNormCoord(f.x2) &&
    isRenderableRemoteNormCoord(f.y2)
  );
}

/** Combo popup position + multiplier sent by native. */
export interface NativeComboPayload {
  /** Normalized 0–1 horizontal position (same space as fruit x). */
  readonly x: number;
  /** Normalized 0–1 vertical position (same space as fruit y). */
  readonly y: number;
  /** How many fruits in this combo. */
  readonly count: number;
}

/** target-attack: authoritative game state from native. */
export type NativeTargetGameState = 'PREPARING' | 'STARTED' | 'PAUSED' | 'GAME_OVER';

export interface NativePlayerSlice {
  readonly keypoints: PoseKeypoint[];
  readonly score: number | null;
  readonly lives?: number | null;
  readonly streak?: number | null;
  readonly combo?: NativeComboPayload | null;
}

export interface NativeTargetPayload {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  /** Radius as fraction of frame height (0–1). */
  readonly radius: number;
  readonly type: 'NORMAL' | 'BOMB' | 'COMBO';
  readonly colorType: 'NORMAL' | 'BOMB' | 'COMBO' | 'PLAYER2' | 'ARCADE_NEXT';
  readonly isHit: boolean;
  readonly lifetimeProgress: number;
  readonly hitAnimationProgress: number;
  readonly arcadeSequenceNumber: number;
  readonly isNextHitInArcade: boolean;
}

export interface NativeLabelPayload {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly alpha: number;
}

/**
 * One WebRTC data-channel frame: wrists/pose + optional authoritative fruits.
 * If `fruits` is **present** (even `[]`), the TV treats this as the remote-display game stream.
 *
 * Optional layout (remote fruit-ninja), same JSON keys as native:
 * - `aspectRatio` — width ÷ height of the logical frame (e.g. 16/9 ≈ **1.77**). TV fits that rect into the canvas (letterbox) and maps normalized x,y inside it.
 * - `fruitSize` — linear size vs **logical height** (e.g. **0.1** = 10% of that height), used for emoji / slice scale on TV.
 * - `normRefWidth` / `normRefHeight` — if `x`/`y` are **pixels** in this buffer, TV divides by these to get 0…1.
 * - `flipX` / `flipY` — mirror normalized space (e.g. texture Y-up). TV `flipY` also via `?normFlipY=1` on tv.html when native omits the flag.
 *
 * HUD fields (fruit-ninja-without-score):
 * - `gameMode` — `CLASSIC` | `ARCADE` | `ZEN`
 * - `score` — current score
 * - `streak` — current streak (consecutive slices without missing)
 * - `lives` — remaining lives; null / absent for non-Classic modes
 * - `timeLeft` — seconds remaining; null / absent for non-Arcade modes
 * - `combo` — floating combo popup position + count; null when no combo to show
 * - `bestScore` — best / high score for HUD (optional)
 */
export interface NativeGameFrame {
  readonly keypoints: PoseKeypoint[];
  readonly fruits?: NativeFruitPayload[];
  /** Logical frame width / height (e.g. 16/9 ≈ 1.77). */
  readonly aspectRatio?: number;
  /** Fruit scale as fraction of logical frame height (e.g. 0.1 = 10%). */
  readonly fruitSize?: number;
  /** Pixel width of the coordinate system (divide raw x when > 1). */
  readonly normRefWidth?: number;
  /** Pixel height of the coordinate system (divide raw y when > 1). */
  readonly normRefHeight?: number;
  /** Mirror horizontal normalized coords (e.g. front camera). */
  readonly flipX?: boolean;
  /** Mirror vertical: maps bottom-left / GL-style Y-up into canvas Y-down. */
  readonly flipY?: boolean;
  /** Game mode driving this frame. */
  readonly gameMode?: 'CLASSIC' | 'ARCADE' | 'ZEN';
  /** Current score. */
  readonly score?: number;
  /** Best / high score for TV HUD (optional). */
  readonly bestScore?: number | null;
  /** Current streak (consecutive non-missed slices). */
  readonly streak?: number;
  /** Remaining lives — Classic only; null / absent otherwise. */
  readonly lives?: number | null;
  /** Seconds remaining — Arcade only; null / absent otherwise. */
  readonly timeLeft?: number | null;
  /** Combo popup to display at normalized position; null = hide. */
  readonly combo?: NativeComboPayload | null;
  /** Game is paused — TV shows pause overlay with current score. */
  readonly isPaused?: boolean;
  /** Game has ended — TV shows game-over overlay with final score. */
  readonly isGameOver?: boolean;
  /** Native asks TV to show the pre-game «Начать» screen again (e.g. after restart). */
  readonly isRestart?: boolean;

  // ── target-attack (optional; when present, TV uses Target Attack renderer) ──
  readonly gameState?: NativeTargetGameState;
  readonly player1?: NativePlayerSlice | null;
  readonly player2?: NativePlayerSlice | null;
  readonly targets?: NativeTargetPayload[];
  readonly labels?: NativeLabelPayload[];
  /**
   * Target Attack: level index from native. Must appear in the **same** JSON passed to
   * `parseNativeGameFrame` (WebRTC DataChannel string **or** Socket `game:frame.rawFrame`) — if Kotlin
   * sends pose-only over P2P and omits this field, TV never receives it.
   */
  readonly currentLevel?: number;
  /**
   * Target Attack: progress within the current level, **0…1** — TV bar fill width (0 = 0%, 1 = 100%).
   * Same delivery rule as `currentLevel`: **not** read from elsewhere; omitting it in the DataChannel
   * payload means the bar stays empty / stale.
   */
  readonly levelProgress?: number;
  /** Target Attack: body detection readiness 0…1 (e.g. calibration). */
  readonly detectionProgress?: number;
  /**
   * Preparation-screen quality hint. When present, TV shows a calibration alert (top-center)
   * until the field is `null` / absent again:
   * - `DARK` — too dark, can't see the player.
   * - `BRIGHT` — too bright.
   * - `LOW_CONF` — low pose-detection confidence.
   */
  readonly calibration?: NativeCalibrationHint | null;
}

export type NativeCalibrationHint = 'DARK' | 'BRIGHT' | 'LOW_CONF';

function parseOptionalFiniteNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function parseNullableFiniteNumber(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const n = parseFloat(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function parseOptionalStringOrNull(v: unknown): string | null | undefined {
  if (v === null) return null;
  if (typeof v === 'string') return v;
  return undefined;
}

/** Int64 / большие id: в JSON как string — иначе IEEE double теряет биты. */
function normalizeFruitId(o: Record<string, unknown>): number | string | undefined {
  const v = o['id'];
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'string') {
    const t = v.trim();
    if (t === '' || !/^-?\d+$/.test(t)) return undefined;
    return t;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    if (Number.isSafeInteger(v)) return v;
    return String(v);
  }
  return undefined;
}

function normalizeFruitEntry(item: unknown): NativeFruitPayload | null {
  if (!item || typeof item !== 'object') return null;
  const o = item as Record<string, unknown>;
  const x = o['x'];
  const y = o['y'];
  const isBomb = o['is_bomb'];
  const isHit = o['is_hit'];
  if (typeof x !== 'number' || typeof y !== 'number') return null;
  const id = normalizeFruitId(o);
  const rot = parseOptionalFiniteNumber(o['rotation']);
  const img = parseOptionalStringOrNull(o['image']);
  const x1 = parseNullableFiniteNumber(o['x1']);
  const y1 = parseNullableFiniteNumber(o['y1']);
  const x2 = parseNullableFiniteNumber(o['x2']);
  const y2 = parseNullableFiniteNumber(o['y2']);
  const r1 = parseNullableFiniteNumber(o['rotation1']);
  const r2 = parseNullableFiniteNumber(o['rotation2']);
  const xu = normalizeCoordFromNativeJson(x);
  const yu = normalizeCoordFromNativeJson(y);
  if (xu === null || yu === null) {
    return null;
  }
  const x1Ok = x1 === null ? null : normalizeCoordFromNativeJson(x1);
  const y1Ok = y1 === null ? null : normalizeCoordFromNativeJson(y1);
  const x2Ok = x2 === null ? null : normalizeCoordFromNativeJson(x2);
  const y2Ok = y2 === null ? null : normalizeCoordFromNativeJson(y2);
  return {
    x: xu,
    y: yu,
    is_bomb: Boolean(isBomb),
    is_hit: Boolean(isHit),
    ...(id !== undefined ? { id } : {}),
    ...(rot !== undefined ? { rotation: rot } : {}),
    ...(img !== undefined ? { image: img } : {}),
    ...(x1Ok !== null ? { x1: x1Ok } : {}),
    ...(y1Ok !== null ? { y1: y1Ok } : {}),
    ...(x2Ok !== null ? { x2: x2Ok } : {}),
    ...(y2Ok !== null ? { y2: y2Ok } : {}),
    ...(r1 !== null ? { rotation1: r1 } : {}),
    ...(r2 !== null ? { rotation2: r2 } : {}),
  } as NativeFruitPayload;
}

/**
 * Parses JSON from native WebRTC DataChannel.
 * - Raw array → legacy pose-only (no fruits key).
 * - Object with `keypoints` and/or `fruits` → game frame.
 */
function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

function parseTargetGameState(v: unknown): NativeTargetGameState | undefined {
  if (v === 'PREPARING' || v === 'STARTED' || v === 'PAUSED' || v === 'GAME_OVER') return v;
  if (typeof v === 'string') {
    const up = v.trim().toUpperCase();
    if (up === 'PREPARING' || up === 'STARTED' || up === 'PAUSED' || up === 'GAME_OVER') {
      return up as NativeTargetGameState;
    }
  }
  return undefined;
}

function parsePlayerSlice(v: unknown): NativePlayerSlice | null | undefined {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (!v || typeof v !== 'object') return undefined;
  const o = v as Record<string, unknown>;
  const kps = o['keypoints'];
  // keypoints may be null when player is off-camera — still a valid slice with score/lives data
  if (kps !== null && !Array.isArray(kps)) return undefined;
  const scoreRaw = o['score'];
  const score =
    scoreRaw === null || scoreRaw === undefined
      ? null
      : parseOptionalFiniteNumber(scoreRaw) ?? null;
  const lives = 'lives' in o ? parseNullableFiniteNumber(o['lives']) : undefined;
  const streak = parseOptionalFiniteNumber(o['streak']);
  const comboRaw = o['combo'];
  let combo: NativeComboPayload | null | undefined;
  if (comboRaw === null) {
    combo = null;
  } else if (comboRaw && typeof comboRaw === 'object' && !Array.isArray(comboRaw)) {
    const c = comboRaw as Record<string, unknown>;
    const cx = parseOptionalFiniteNumber(c['x']);
    const cy = parseOptionalFiniteNumber(c['y']);
    const count = parseOptionalFiniteNumber(c['count']);
    if (cx !== undefined && cy !== undefined && count !== undefined) {
      combo = { x: cx, y: cy, count };
    }
  }
  const slice: { -readonly [K in keyof NativePlayerSlice]?: NativePlayerSlice[K] } = {
    keypoints: Array.isArray(kps) ? normalizePose(kps) : [],
    score,
  };
  if (lives !== undefined) slice.lives = lives;
  if (streak !== undefined) slice.streak = streak;
  if (combo !== undefined) slice.combo = combo;
  return slice as NativePlayerSlice;
}

function parseTargetKind(v: unknown): 'NORMAL' | 'BOMB' | 'COMBO' {
  if (v === 'NORMAL' || v === 'BOMB' || v === 'COMBO') return v;
  if (typeof v === 'string') {
    const up = v.toUpperCase();
    if (up === 'NORMAL' || up === 'BOMB' || up === 'COMBO') return up as 'NORMAL' | 'BOMB' | 'COMBO';
  }
  return 'NORMAL';
}

function parseTargetColorType(v: unknown): NativeTargetPayload['colorType'] {
  if (
    v === 'NORMAL' ||
    v === 'BOMB' ||
    v === 'COMBO' ||
    v === 'PLAYER2' ||
    v === 'ARCADE_NEXT'
  ) {
    return v;
  }
  if (typeof v === 'string') {
    const up = v.toUpperCase();
    if (up === 'NORMAL' || up === 'BOMB' || up === 'COMBO' || up === 'PLAYER2' || up === 'ARCADE_NEXT') {
      return up as NativeTargetPayload['colorType'];
    }
  }
  return 'NORMAL';
}

function parseTargetEntry(item: unknown): NativeTargetPayload | null {
  if (!item || typeof item !== 'object') return null;
  const o = item as Record<string, unknown>;
  const idRaw = o['id'];
  const id = idRaw === null || idRaw === undefined ? '' : String(idRaw);
  const xRaw = o['x'];
  const yRaw = o['y'];
  if (typeof xRaw !== 'number' || typeof yRaw !== 'number') return null;
  const x = normalizeCoordFromNativeJson(xRaw);
  const y = normalizeCoordFromNativeJson(yRaw);
  const radiusRaw = parseOptionalFiniteNumber(o['radius']);
  const radius = radiusRaw !== undefined ? normalizeRadiusFromNativeJson(radiusRaw) : null;
  if (x === null || y === null || radius === null) return null;
  return {
    id,
    x,
    y,
    radius,
    type: parseTargetKind(o['type']),
    colorType: parseTargetColorType(o['colorType'] ?? o['color_type']),
    isHit: Boolean(o['isHit'] ?? o['is_hit']),
    lifetimeProgress: clamp01(
      parseOptionalFiniteNumber(o['lifetimeProgress'] ?? o['lifetime_progress']) ?? 0,
    ),
    hitAnimationProgress: clamp01(
      parseOptionalFiniteNumber(o['hitAnimationProgress'] ?? o['hit_animation_progress']) ?? 0,
    ),
    arcadeSequenceNumber: Math.round(
      parseOptionalFiniteNumber(o['arcadeSequenceNumber'] ?? o['arcade_sequence_number']) ?? 0,
    ),
    isNextHitInArcade: Boolean(o['isNextHitInArcade'] ?? o['is_next_hit_in_arcade']),
  };
}

function parseLabelEntry(item: unknown): NativeLabelPayload | null {
  if (!item || typeof item !== 'object') return null;
  const o = item as Record<string, unknown>;
  const text = o['text'] ?? o['label'];
  const lx = o['x'];
  const ly = o['y'];
  if (typeof lx !== 'number' || typeof ly !== 'number') return null;
  const x = normalizeCoordFromNativeJson(lx);
  const y = normalizeCoordFromNativeJson(ly);
  const colorRaw = o['color'];
  const alpha = parseOptionalFiniteNumber(o['alpha']);
  if (typeof text !== 'string' || x === null || y === null) {
    return null;
  }
  const color =
    typeof colorRaw === 'string' && colorRaw.trim() !== ''
      ? colorRaw.trim()
      : '#ffffff';
  return {
    text,
    x,
    y,
    color,
    alpha: alpha !== undefined ? clamp01(alpha) : 1,
  };
}

type TargetAttackExtras = Pick<
  NativeGameFrame,
  | 'gameState'
  | 'player1'
  | 'player2'
  | 'targets'
  | 'labels'
  | 'currentLevel'
  | 'levelProgress'
  | 'detectionProgress'
  | 'isPaused'
  | 'isGameOver'
>;

function mergeTargetAttackFields(o: Record<string, unknown>): TargetAttackExtras {
  const out: { -readonly [K in keyof TargetAttackExtras]?: TargetAttackExtras[K] } = {};
  const gs = parseTargetGameState(o['gameState'] ?? o['game_state']);
  if (gs !== undefined) {
    out.gameState = gs;
    if (gs === 'PAUSED') out.isPaused = true;
    if (gs === 'GAME_OVER') out.isGameOver = true;
    if (gs === 'STARTED' || gs === 'PREPARING') out.isPaused = false;
  }
  const p1 = parsePlayerSlice(o['player1']);
  if (p1 !== undefined) out.player1 = p1;
  const p2 = parsePlayerSlice(o['player2']);
  if (p2 !== undefined) out.player2 = p2;
  const tg = o['targets'];
  if (Array.isArray(tg)) {
    out.targets = tg.map(parseTargetEntry).filter((t): t is NativeTargetPayload => t !== null);
  }
  const lb = o['labels'];
  if (Array.isArray(lb)) {
    out.labels = lb.map(parseLabelEntry).filter((l): l is NativeLabelPayload => l !== null);
  }
  const cl = parseOptionalFiniteNumber(o['currentLevel'] ?? o['current_level']);
  if (cl !== undefined) out.currentLevel = Math.round(cl);
  const lp = parseOptionalFiniteNumber(o['levelProgress'] ?? o['level_progress']);
  if (lp !== undefined) {
    const n = lp > 1 && lp <= 100 ? lp / 100 : lp;
    out.levelProgress = clamp01(n);
  }
  const dp = parseOptionalFiniteNumber(o['detectionProgress'] ?? o['detection_progress']);
  if (dp !== undefined) {
    const n = dp > 1 && dp <= 100 ? dp / 100 : dp;
    out.detectionProgress = clamp01(n);
  }
  return out;
}

/**
 * Kotlin / relay sometimes put `currentLevel` / `levelProgress` only under `content`, `meta`, or `payload`.
 * Inner bags merged first; **top-level fields win** so explicit JSON from native overrides.
 */
function mergeTargetAttackFieldsDeep(o: Record<string, unknown>): TargetAttackExtras {
  let acc: TargetAttackExtras = {};
  for (const key of ['meta', 'data', 'payload', 'content', 'message', 'body'] as const) {
    const v = o[key];
    if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
    acc = { ...acc, ...mergeTargetAttackFields(v as Record<string, unknown>) };
  }
  return { ...acc, ...mergeTargetAttackFields(o) };
}

/** True when the frame carries Target Attack fields (gameplay slices, targets, or TA HUD/meta). */
export function isTargetAttackNativeFrame(f: NativeGameFrame): boolean {
  return (
    f.targets !== undefined ||
    f.labels !== undefined ||
    f.player1 !== undefined ||
    f.player2 !== undefined ||
    f.gameState !== undefined ||
    f.currentLevel !== undefined ||
    f.levelProgress !== undefined ||
    f.detectionProgress !== undefined
  );
}

/**
 * Merged pose for TV: non-empty top-level `keypoints`, else `player1` / `player2` (same JSON shape as Target Attack).
 */
export function resolveLegacyPoseKeypoints(frame: NativeGameFrame): readonly PoseKeypoint[] {
  if (frame.keypoints.length > 0) {
    return frame.keypoints;
  }
  const k1 = frame.player1?.keypoints;
  if (k1 && k1.length > 0) {
    return k1;
  }
  const k2 = frame.player2?.keypoints;
  if (k2 && k2.length > 0) {
    return k2;
  }
  return frame.keypoints;
}

/**
 * Parses JSON from native WebRTC DataChannel.
 * - Raw array → legacy pose-only (no fruits key).
 * - Object with `keypoints` and/or `fruits` → game frame.
 */
export function parseNativeGameFrame(raw: unknown): NativeGameFrame | null {
  if (raw === null || raw === undefined) return null;

  if (Array.isArray(raw)) {
    if (raw.length >= 2 && isTrailingMetaObject(raw[raw.length - 1])) {
      const meta = raw[raw.length - 1] as Record<string, unknown>;
      const poseSlice = raw.slice(0, -1);
      const keypoints = normalizePose(poseSlice);
      const extras = parseLayoutFields(meta);
      const ta = mergeTargetAttackFieldsDeep(meta);
      if (!('fruits' in meta)) {
        return { keypoints, ...extras, ...ta };
      }
      const fr = meta['fruits'];
      if (!Array.isArray(fr)) {
        return { keypoints, fruits: [], ...extras, ...ta };
      }
      const fruits = fr.map(normalizeFruitEntry).filter((f): f is NativeFruitPayload => f !== null);
      return { keypoints, fruits, ...extras, ...ta };
    }
    const keypoints = normalizePose(raw);
    return { keypoints };
  }

  if (typeof raw !== 'object') return null;
  const o = unwrapNestedGamePayload(raw as Record<string, unknown>);

  const p1 = o['player1'];
  const p2 = o['player2'];
  const k1 =
    p1 && typeof p1 === 'object' && Array.isArray((p1 as Record<string, unknown>)['keypoints'])
      ? normalizePose((p1 as Record<string, unknown>)['keypoints'])
      : [];
  const k2 =
    p2 && typeof p2 === 'object' && Array.isArray((p2 as Record<string, unknown>)['keypoints'])
      ? normalizePose((p2 as Record<string, unknown>)['keypoints'])
      : [];

  let keypoints: PoseKeypoint[] = [];
  if (Array.isArray(o['keypoints'])) {
    keypoints = normalizePose(o['keypoints']);
  }
  if (keypoints.length === 0) {
    if (k1.length > 0 || k2.length > 0) {
      keypoints = [...k1, ...k2];
    } else if (!Array.isArray(o['keypoints'])) {
      keypoints = normalizePose(raw);
    }
  }

  const layoutHud = parseLayoutFields(o);
  const taExtras = mergeTargetAttackFieldsDeep(o);
  const base: NativeGameFrame = { keypoints, ...layoutHud, ...taExtras };

  if (!('fruits' in o)) {
    return base;
  }

  const fr = o['fruits'];
  if (!Array.isArray(fr)) {
    return { ...base, fruits: [] };
  }

  const fruits = fr.map(normalizeFruitEntry).filter((f): f is NativeFruitPayload => f !== null);
  return { ...base, fruits };
}

/** Accepts JSON numbers, numeric strings, and ratio strings like `"16/9"`. */
function parsePositiveNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    const ratio = s.match(/^(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)$/);
    if (ratio) {
      const a = parseFloat(ratio[1]!);
      const b = parseFloat(ratio[2]!);
      if (Number.isFinite(a) && Number.isFinite(b) && b > 0 && a > 0) return a / b;
    }
    const n = parseFloat(s);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return undefined;
}

function parseLayoutFromRecord(o: Record<string, unknown>): Pick<NativeGameFrame, 'aspectRatio' | 'fruitSize'> {
  const aspectRatio =
    parsePositiveNumber(o['aspectRatio']) ??
    parsePositiveNumber(o['aspect_ratio']) ??
    parsePositiveNumber(o['aspect']);
  const fruitSize =
    parsePositiveNumber(o['fruitSize']) ??
    parsePositiveNumber(o['fruit_size']) ??
    parsePositiveNumber(o['size']);
  const out: { aspectRatio?: number; fruitSize?: number } = {};
  if (aspectRatio !== undefined) out.aspectRatio = aspectRatio;
  if (fruitSize !== undefined) out.fruitSize = fruitSize;
  return out;
}

function parseNormFromRecord(o: Record<string, unknown>): Pick<
  NativeGameFrame,
  'normRefWidth' | 'normRefHeight' | 'flipX' | 'flipY'
> {
  const rw =
    parsePositiveNumber(o['normRefWidth']) ??
    parsePositiveNumber(o['norm_ref_width']) ??
    parsePositiveNumber(o['bufferWidth']) ??
    parsePositiveNumber(o['imageWidth']);
  const rh =
    parsePositiveNumber(o['normRefHeight']) ??
    parsePositiveNumber(o['norm_ref_height']) ??
    parsePositiveNumber(o['bufferHeight']) ??
    parsePositiveNumber(o['imageHeight']);
  const flipX = parseJsonBool(o['flipX'] ?? o['flip_x']);
  const flipY = parseJsonBool(o['flipY'] ?? o['flip_y'] ?? o['invertY'] ?? o['invert_y']);
  const out: {
    normRefWidth?: number;
    normRefHeight?: number;
    flipX?: boolean;
    flipY?: boolean;
  } = {};
  if (rw !== undefined) out.normRefWidth = rw;
  if (rh !== undefined) out.normRefHeight = rh;
  if (flipX !== undefined) out.flipX = flipX;
  if (flipY !== undefined) out.flipY = flipY;
  return out;
}

function parseJsonBool(v: unknown): boolean | undefined {
  if (v === true || v === false) return v;
  if (v === 1 || v === 0) return Boolean(v);
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === '1' || s === 'yes') return true;
    if (s === 'false' || s === '0' || s === 'no') return false;
  }
  return undefined;
}

type FrameLayoutExtras = Pick<
  NativeGameFrame,
  'aspectRatio' | 'fruitSize' | 'normRefWidth' | 'normRefHeight' | 'flipX' | 'flipY'
>;

type FrameHudExtras = Pick<
  NativeGameFrame,
  | 'gameMode'
  | 'score'
  | 'bestScore'
  | 'streak'
  | 'lives'
  | 'timeLeft'
  | 'combo'
  | 'isPaused'
  | 'isGameOver'
  | 'isRestart'
  | 'calibration'
>;

export function parseCalibrationHint(v: unknown): NativeCalibrationHint | null | undefined {
  if (v === null) return null;
  if (typeof v !== 'string') return undefined;
  const up = v.trim().toUpperCase();
  if (up === 'DARK' || up === 'BRIGHT' || up === 'LOW_CONF') return up as NativeCalibrationHint;
  if (up === '') return null;
  return undefined;
}

function parseGameMode(v: unknown): NativeGameFrame['gameMode'] {
  if (v === 'CLASSIC' || v === 'ARCADE' || v === 'ZEN') return v;
  if (typeof v === 'string') {
    const up = v.toUpperCase();
    if (up === 'CLASSIC' || up === 'ARCADE' || up === 'ZEN') return up as NativeGameFrame['gameMode'];
  }
  return undefined;
}

function parseHudFields(o: Record<string, unknown>): FrameHudExtras {
  const out: { -readonly [K in keyof FrameHudExtras]?: FrameHudExtras[K] } = {};
  const mode = parseGameMode(o['gameMode'] ?? o['game_mode'] ?? o['mode']);
  if (mode !== undefined) out.gameMode = mode;
  const score = parseOptionalFiniteNumber(o['score']);
  if (score !== undefined) out.score = score;
  if ('bestScore' in o || 'best_score' in o) {
    out.bestScore = parseNullableFiniteNumber(o['bestScore'] ?? o['best_score']);
  }
  const streak = parseOptionalFiniteNumber(o['streak']);
  if (streak !== undefined) out.streak = streak;
  const lives = parseNullableFiniteNumber(o['lives']);
  out.lives = lives;
  const timeLeft = parseNullableFiniteNumber(o['timeLeft'] ?? o['time_left']);
  out.timeLeft = timeLeft;
  const comboRaw = o['combo'];
  if (comboRaw === null) {
    out.combo = null;
  } else if (comboRaw && typeof comboRaw === 'object' && !Array.isArray(comboRaw)) {
    const c = comboRaw as Record<string, unknown>;
    const cx = parseOptionalFiniteNumber(c['x']);
    const cy = parseOptionalFiniteNumber(c['y']);
    const count = parseOptionalFiniteNumber(c['count']);
    if (cx !== undefined && cy !== undefined && count !== undefined) {
      out.combo = { x: cx, y: cy, count };
    }
  }
  const isPaused = parseJsonBool(o['isPaused'] ?? o['is_paused']);
  if (isPaused !== undefined) out.isPaused = isPaused;
  const isGameOver = parseJsonBool(o['isGameOver'] ?? o['is_game_over']);
  if (isGameOver !== undefined) out.isGameOver = isGameOver;
  const isRestart = parseJsonBool(o['isRestart'] ?? o['is_restart']);
  if (isRestart !== undefined) out.isRestart = isRestart;
  if ('calibration' in o) {
    const cal = parseCalibrationHint(o['calibration']);
    if (cal !== undefined) out.calibration = cal;
  }
  return out;
}

/** Merges layout, norm refs, flips from top-level + nested bags. Later keys win. */
function parseLayoutFields(o: Record<string, unknown>): FrameLayoutExtras & FrameHudExtras {
  let merged: FrameLayoutExtras & FrameHudExtras = {
    ...parseLayoutFromRecord(o),
    ...parseNormFromRecord(o),
    ...parseHudFields(o),
  };
  for (const key of ['layout', 'meta', 'viewport', 'game', 'gameConfig'] as const) {
    const v = o[key];
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const rec = v as Record<string, unknown>;
      const innerL = parseLayoutFromRecord(rec);
      const innerN = parseNormFromRecord(rec);
      merged = { ...merged, ...innerL, ...innerN, ...parseHudFields(rec) };
    }
  }
  return merged;
}

/** Last array element is metadata (aspect / fruits) — not a keypoint. */
function isTrailingMetaObject(item: unknown): boolean {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
  const o = item as Record<string, unknown>;
  if (typeof o['type'] === 'string' && o['type'].length > 0) {
    const looksLikeKp = typeof o['x'] === 'number' && typeof o['y'] === 'number';
    if (looksLikeKp) return false;
  }
  return (
    'aspectRatio' in o ||
    'aspect_ratio' in o ||
    'aspect' in o ||
    'fruitSize' in o ||
    'fruit_size' in o ||
    'fruits' in o ||
    'targets' in o ||
    'player1' in o ||
    'layout' in o ||
    'meta' in o ||
    'normRefWidth' in o ||
    'norm_ref_width' in o ||
    'flipX' in o ||
    'flip_x' in o ||
    'flipY' in o ||
    'flip_y' in o
  );
}

/** Some clients wrap the game in `{ "data": { ... } }` — merge inner fields for parsing. */
function unwrapNestedGamePayload(o: Record<string, unknown>): Record<string, unknown> {
  for (const key of ['data', 'frame', 'payload', 'message', 'body', 'content'] as const) {
    const v = o[key];
    if (!v || typeof v !== 'object' || Array.isArray(v)) continue;
    const inner = v as Record<string, unknown>;
    const hasGame =
      'keypoints' in inner ||
      'fruits' in inner ||
      'targets' in inner ||
      'player1' in inner ||
      'gameState' in inner ||
      'game_state' in inner ||
      'currentLevel' in inner ||
      'current_level' in inner ||
      'levelProgress' in inner ||
      'level_progress' in inner ||
      'aspectRatio' in inner ||
      'aspect_ratio' in inner ||
      'layout' in inner ||
      'meta' in inner ||
      'normRefWidth' in inner ||
      'flipY' in inner;
    if (hasGame) return { ...o, ...inner };
  }
  return o;
}
